import { useQuery } from '@tanstack/react-query';
import { ValueOf } from 'type-fest';

import {
  type QueryFunction,
  useRequest,
  type ResolutionAppliedResponse,
} from '@frontend/api';

import type {
  AsyncTasks,
  RelatedEntityType,
  TaskEntityType,
  TaskPipeline,
  TaskResolution,
  TaskStatus,
} from './const';

export type TaskStatusType = ValueOf<typeof TaskStatus>;
export type TaskResolutionType = ValueOf<typeof TaskResolution>;
export type TaskPipelineType = ValueOf<typeof TaskPipeline>;
export type TaskType = ValueOf<typeof AsyncTasks>;

type EntityKey = string;
export type TaskId = string;

export const DEFAULT_TASK_ID = 'default';

export type TaskStatusObject = {
  status: TaskStatusType;
  resolution?: TaskResolutionType;
  stamp: number; // unix timestamp
  pipeline?: TaskPipelineType;
};

export type TaskStatusCacheEntry = Record<
  TaskType,
  Record<TaskId, TaskStatusObject>
>;

export type ExtractedTableTaskMetaType = {
  totalTableCount: number;
  bomTableCount: number;
};

export type BulkResolveActionsTaskMetaType = {
  updateCount: number;
  appliedResponse: ResolutionAppliedResponse;
};

export type ExtractedTableDismissedMetaType = {
  extractedTableId: number;
};

type TaskStatusMeta =
  | Record<string, unknown>
  | ExtractedTableTaskMetaType
  | BulkResolveActionsTaskMetaType
  | ExtractedTableDismissedMetaType;

/**
 * Task entity → Related entity
 *
 * An object describing all Related entity objects associated to a Task entity.
 *
 * Record of all related entity pks from the resource the task is associated to (e.g.
 * PartFile, PrintPage, Component, etc.) to entities that may need to show a loading
 * state or refresh themselves when updates occur to the task.
 *
 * Generally, these related entities are immediately adjacent foreign key relationships
 * or follow ManyToMany relationship chains.
 *
 * For example on the PartFile record:
 *     - PartFile -> Part -> Component -> QuoteItem -> Quote
 *     - PartFile -> PartFileToQuote -> Quote
 *     - PartFile -> PartMaster
 *
 * For example on Component record:
 *     - Component -> QuoteItem -> Quote
 *     - Component -> Part
 */
export type RelatedEntityPks = {
  entityType: ValueOf<typeof TaskEntityType>;
  entityKey: EntityKey;
  partMasterUuids?: string[];
  partUuids?: string[];
  componentIds?: number[];
  quoteItemIds?: number[];
  quoteIds?: number[];
  partFileToQuoteQuoteIds?: number[];
};

export type TaskStatusUpdateMessage = {
  entityKey: EntityKey;
  status: TaskStatusType;
  resolution?: TaskResolutionType;
  meta?: TaskStatusMeta;
  pipeline?: TaskPipelineType;
  task: TaskType;
  taskKey?: TaskId | null;
  stamp: number; // unix timestamp
  relatedEntityPks: RelatedEntityPks;
};

type TaskStatusCacheEntryForCheck = {
  entityKey: EntityKey;
  status: TaskStatusCacheEntry;
};

/**
 * Related entity → Task entity
 *
 * An object describing all Task entity objects associated to a Related entity object.
 *
 * This is essentially the inverted index of the Related entity pks. Used to create an
 * inverted index of what entities need to be "watched" by the client as new messages
 * come in.
 *
 * This object is fetched on initialization and updated with more related entities as
 * more messages come in.
 */
export type TaskEntityKeys = {
  [TaskEntityType.PART_FILE]: string[];
  [TaskEntityType.COMPONENT]: string[];
  [TaskEntityType.QUOTE]: string[];
};

export type TaskEntityKeysWithMeta = TaskEntityKeys & {
  entityType: ValueOf<typeof RelatedEntityType>;
  entityPk: string;
};

export type FetchTaskStatusesResponse = {
  taskEntityKeys: TaskEntityKeysWithMeta[];
  [TaskEntityType.PART_FILE]: TaskStatusCacheEntryForCheck[];
  [TaskEntityType.COMPONENT]: TaskStatusCacheEntryForCheck[];
  [TaskEntityType.QUOTE]: TaskStatusCacheEntryForCheck[];
};

type FetchTaskStatusesParams = {
  componentIds?: number[];
  quoteNumberWithRevision?: string;
};

export const TASK_STATUS_BASE_QUERY_KEY = ['taskStatus'];

type FetchTaskStatusesQueryKeyType = readonly [
  ...typeof TASK_STATUS_BASE_QUERY_KEY,
  FetchTaskStatusesParams,
];

const getFetchTaskStatusesQueryKey = (
  params: FetchTaskStatusesParams,
): FetchTaskStatusesQueryKeyType => {
  return [...TASK_STATUS_BASE_QUERY_KEY, params];
};

export const useFetchTaskStatuses: QueryFunction<
  FetchTaskStatusesResponse,
  [FetchTaskStatusesParams],
  FetchTaskStatusesQueryKeyType
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryKey = getFetchTaskStatusesQueryKey(params);

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) =>
      request<FetchTaskStatusesResponse>(`/task_status/`, {
        init: {
          method: 'POST',
          body: params,
          signal,
        },
      }),
  });
};
